import React from 'react';
import {
  Banknote,
  CreditCard,
  Ticket,
  FileText,
  AlertTriangle,
  Check
} from 'lucide-react';
import { formatCurrency, sanitizeAmountInput } from '../../../utils/currency';
import { ChannelDefinition } from '../../../types/registerClosure.types';

interface PaymentChannelsSectionProps {
  channels: ChannelDefinition[];
  enteredAmounts: Record<string, string>;
  setEnteredAmounts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  getSystemTotal: (id: string) => number;
  getDiscrepancy: (id: string) => number;
  getChannelOpsCount: (id: string) => { sales: number; returns: number; totalOps: number };
}

function getChannelIcon(id: string) {
  switch (id) {
    case 'cash': return <Banknote className="w-6 h-6" />;
    case 'card': return <CreditCard className="w-6 h-6" />;
    case 'marcial_card': return <CreditCard className="w-6 h-6" />;
    case 'gift_card': return <Ticket className="w-6 h-6" />;
    default: return <FileText className="w-6 h-6" />;
  }
}

export function PaymentChannelsSection({
  channels,
  enteredAmounts,
  setEnteredAmounts,
  getSystemTotal,
  getDiscrepancy,
  getChannelOpsCount
}: PaymentChannelsSectionProps) {
  return (
    <div className="rcv-channels">
      <p className="rcv-channels-title">Declaración de Medios de Pago Contados</p>

      {channels.map((chan) => {
        const expected = getSystemTotal(chan.id);
        const enteredVal = enteredAmounts[chan.id];
        const diff = getDiscrepancy(chan.id);
        const ops = getChannelOpsCount(chan.id);
        const isMatch = Math.abs(diff) < 0.01;

        return (
          <div
            key={chan.id}
            className={`rcv-channel-card ${isMatch ? 'rcv-channel-card--match' : 'rcv-channel-card--mismatch'}`}
          >
            <div className="rcv-channel-left">
              <div className={`rcv-channel-icon rcv-channel-icon--${chan.id}`}>
                {getChannelIcon(chan.id)}
              </div>
              <div>
                <h3 className="rcv-channel-name">{chan.label}</h3>
                <p className="rcv-channel-desc">{chan.desc}</p>
                <div className="rcv-channel-meta">
                  <span className="rcv-channel-ops">
                    {ops.totalOps} op ({ops.sales} v. / {ops.returns} d.)
                  </span>
                  <span className="rcv-channel-system">
                    Sistema: <span className="mono">{formatCurrency(expected)}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="rcv-channel-right">
              <div className="rcv-channel-input-group">
                <span className="rcv-channel-input-label">Monto Físico Declarado</span>
                <div className="rcv-channel-input-wrap">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={enteredVal}
                    onChange={(e) => {
                      const val = sanitizeAmountInput(e.target.value);
                      setEnteredAmounts(prev => ({ ...prev, [chan.id]: val }));
                    }}
                    className="rcv-channel-input"
                  />
                  <span className="rcv-channel-euro">€</span>
                </div>
              </div>

              <div className="rcv-status-circle">
                {isMatch ? (
                  <div className="rcv-status-inner rcv-status-inner--ok" title="Cuadrante correcto">
                    <Check className="w-5 h-5" />
                  </div>
                ) : (
                  <div className="rcv-status-inner rcv-status-inner--bad" title={'Descuadre de ' + formatCurrency(diff)}>
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
